/**
 * MODULE: domain/pages/frontend/utils/pageCallPreviewUtils.ts
 * PURPOSE: Normalize call API payloads for transcript preview blocks
 */

import type { PageCallListItem, PageCallTranscriptPreview } from '../../contracts/pages.types';

type RawCall = {
  id?: string;
  direction?: string | null;
  status?: string | null;
  from_number?: string | null;
  to_number?: string | null;
  initiated_at?: string | null;
  ended_at?: string | null;
  duration_seconds?: number | null;
  transcription_text?: string | null;
  transcription_status?: string | null;
  summary?: string | null;
  sentiment_score?: number | null;
  notes?: string | null;
};

type RawAnalysis = {
  summary?: string | null;
  sentiment_score?: number | null;
  topics?: string[] | null;
  action_items?: unknown[] | null;
};

export const TRANSCRIPT_EXCERPT_LENGTH = 320;

export function formatCallPhoneLabel(fromNumber?: string | null, toNumber?: string | null): string {
  const from = fromNumber?.trim();
  const to = toNumber?.trim();
  if (from && to) return `${from} → ${to}`;
  return from || to || 'Recorded call';
}

export function formatCallDuration(seconds?: number | null): string | null {
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  if (minutes <= 0) return `${remainder}s`;
  return `${minutes}m ${remainder.toString().padStart(2, '0')}s`;
}

export function formatCallOccurredAt(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function buildCallListItemLabel(call: RawCall): string {
  const notes = call.notes?.trim();
  if (notes) return notes;
  return formatCallPhoneLabel(call.from_number, call.to_number);
}

export function buildCallListItemSubtitle(call: RawCall): string | null {
  const parts = [
    formatCallOccurredAt(call.initiated_at),
    formatCallDuration(call.duration_seconds),
    call.direction ? call.direction.replace(/_/g, ' ') : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function excerptTranscript(text?: string | null, maxLength = TRANSCRIPT_EXCERPT_LENGTH): string | null {
  const normalized = text?.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}…`;
}

export function sentimentLabel(score?: number | null): string | null {
  if (score == null || Number.isNaN(score)) return null;
  if (score >= 0.35) return 'Positive';
  if (score <= -0.35) return 'Negative';
  return 'Neutral';
}

export function sentimentBadgeClass(score?: number | null): string {
  const label = sentimentLabel(score);
  if (label === 'Positive') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  if (label === 'Negative') {
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

export function normalizeCallListItem(call: RawCall): PageCallListItem | null {
  const id = call.id?.trim();
  if (!id) return null;
  return {
    id,
    label: buildCallListItemLabel(call),
    subtitle: buildCallListItemSubtitle(call),
    occurredAt: call.initiated_at ?? null,
  };
}

export function normalizeCallTranscriptPreview(
  call: RawCall,
  analysis?: RawAnalysis | null
): PageCallTranscriptPreview {
  const callId = call.id?.trim() ?? '';
  const summary = analysis?.summary?.trim() || call.summary?.trim() || null;
  const transcript = call.transcription_text?.trim() || null;
  const sentimentScore =
    analysis?.sentiment_score ?? call.sentiment_score ?? null;

  return {
    callId,
    label: buildCallListItemLabel(call),
    occurredAt: call.initiated_at ?? null,
    durationSeconds: call.duration_seconds ?? null,
    direction: call.direction ?? null,
    summary,
    transcriptExcerpt: excerptTranscript(transcript),
    fullTranscript: transcript,
    transcriptStatus: call.transcription_status ?? null,
    sentimentScore,
    topics: Array.isArray(analysis?.topics) ? analysis!.topics!.filter(Boolean) : [],
    actionItemCount: Array.isArray(analysis?.action_items) ? analysis!.action_items!.length : 0,
  };
}
